<template>
  <div class="error-record-container">
    <TableList
      v-rect-loading="loading"
      class="error-record-list"
      row-key="id"
      :showCreateBtn="false"
      :title="$t(`${i18nPrefix}.label.errorRecordList`)"
      :listData="errorRecordList"
      :tableColumns="tableColumns"
      :actionBtn="actionBtn"
      :pagination="pagination"
      @pagination-change="handlePaginationChange"
    >
      <template #filter>
        <el-select
          v-if="isSupperAdminCompany"
          v-model="search.root_company_id"
          clearable
          filterable
          :placeholder="$t(`${i18nPrefix}.placeholder.selectCompany`)"
          @change="handleSearchChange"
        >
          <el-option
            v-for="(item, key) in companyList"
            :key="key"
            :label="item.i18nName"
            :value="item.id"
          />
        </el-select>
        <div class="date-select">
          <el-date-picker
            v-model="startEndTimeList"
            type="datetimerange"
            align="right"
            format="yyyy-MM-dd HH:mm"
            value-format="yyyy-MM-dd HH:mm"
            prefix-icon="el-icon-time"
            range-separator="-"
            :start-placeholder="$t(`${i18nPrefix}.label.startDate`)"
            :end-placeholder="$t(`${i18nPrefix}.label.endDate`)"
            :default-value="defaultDateRange"
            :picker-options="pickerOptions"
            @change="handleSearchChange"
          >
          </el-date-picker>
        </div>
        <el-select
          v-model="search.process_status"
          clearable
          filterable
          :placeholder="$t(`${i18nPrefix}.placeholder.selectProcessStatus`)"
          @change="handleSearchChange"
        >
          <el-option
            v-for="(item, key) in processStatus"
            :key="key"
            :label="item.label"
            :value="item.key"
          />
        </el-select>
        <el-select
          v-model="search.sync_job_type"
          clearable
          filterable
          :placeholder="$t(`${i18nPrefix}.placeholder.selectType`)"
          @change="handleSearchChange"
        >
          <el-option
            v-for="(item, key) in integrationType"
            :key="key"
            :label="item.label"
            :value="item.key"
          />
        </el-select>
        <el-button v-rect-loading="exportStatus.loading" class="blue-btn export-btn" @click="handleExport">{{ $t(`${i18nPrefix}.btn.export`) }}</el-button>
      </template>
      <template #candidateName="{data, row}">
        <div v-auto-tooltip class="candidate-name" :class="[!isSupperAdminCompany&&'has-click']" @click="handleOpenProfile(row)">{{ data }}</div>
      </template>
    </TableList>
    <DialogWrapper
      close-on-cancel
      hideFooter
      class="error-info-dialog"
      :show.sync="errorInfoDialog.show"
      :titleI18nKey="`${i18nPrefix}.dialog.syncErrorDetail`"
    >
      <div v-if="errorInfoDialog.content" class="error-info">{{ formatXmlContent(errorInfoDialog.content) }}</div>
      <div v-else class="error-info">{{ $t(`${i18nPrefix}.message.noErrorInfo`) }}</div>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { addI18nProperty } from '@/i18n'
import { DATE_PICKER_SHORTCUTS, INTEGRATION_TYPE } from '@/constant/common'
import { AsyncTask } from '@/utils/common'

export default {
  components: {
    TableList,
    DialogWrapper
  },
  props: {
    companyList: {
      type: Array,
      default: () => []
    },
    companyId: {
      type: Number,
      required: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.apScheduler',
      defaultDateRange: [
        moment().subtract(1, 'month').startOf('month').toDate(),
        moment().toDate()
      ],
      loading: false,
      errorRecordList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        pageSizes: [10, 50, 100],
        total: 0
      },
      search: {
        sync_job_type: null,
        start_date: null,
        end_date: null,
        process_status: 'UNPROCESSED',
        root_company_id: null
      },
      errorInfoDialog: {
        show: false,
        content: null
      },
      exportStatus: {
        loading: false
      },
      actionBtn: {
        viewErrorDetail: {
          labelI18nKey: 'admin.apScheduler.btn.viewErrorDetail',
          show: (row) => { return row.process_status === 'UNPROCESSED' },
          onclick: this.handleViewErrorDetail
        },
        markErrorHasProcessed: {
          labelI18nKey: 'admin.apScheduler.btn.markErrorHasProcessed',
          show: (row) => { return row.process_status === 'UNPROCESSED' },
          onclick: this.handleMarkErrorHasProcessed
        }
      }
    }
  },
  computed: {
    processStatus () {
      return [
        {
          key: 'UNPROCESSED',
          label: this.$t(`${this.i18nPrefix}.PROCESS_STATUS.UNPROCESSED`)
        },
        {
          key: 'PROCESSED',
          label: this.$t(`${this.i18nPrefix}.PROCESS_STATUS.PROCESSED`)
        }
      ]
    },
    integrationType () {
      return [
        {
          key: INTEGRATION_TYPE.SYNC_JOB_FROM,
          label: this.$t(`${this.i18nPrefix}.label.jobSync`)
        },
        {
          key: INTEGRATION_TYPE.SYNC_APPLICANT_TO,
          label: this.$t(`${this.i18nPrefix}.label.applicationSync`)
        },
        {
          key: INTEGRATION_TYPE.PULL_APPLICANT_FROM,
          label: this.$t(`${this.i18nPrefix}.label.applicationPullSync`)
        },
        {
          key: INTEGRATION_TYPE.SYNC_EMPLOYEE_FROM,
          label: this.$t(`${this.i18nPrefix}.label.employeePullSync`)
        },
        {
          key: INTEGRATION_TYPE.SYNC_APPLICANT_STATUS_FROM,
          label: this.$t(`${this.i18nPrefix}.label.applicationStatusPullSync`)
        },
        {
          key: INTEGRATION_TYPE.SYNC_APPLICANT_STATUS_TO,
          label: this.$t(`${this.i18nPrefix}.label.applicationStatusPushSync`)
        }
      ]
    },
    startEndTimeList: {
      get () {
        const list = []
        if (this.search.start_date && this.search.end_date) {
          list[0] = this.search.start_date
          list[1] = this.search.end_date
        }
        return list
      },
      set (val) {
        this.search.start_date = val ? val[0] : null
        this.search.end_date = val ? val[1] : null
      }
    },
    pickerOptions () {
      return {
        shortcuts: _.map(DATE_PICKER_SHORTCUTS, (item, key) => ({
          text: this.$t(`common.dateShortcut.${key}`),
          onClick (picker) {
            const start = moment().startOf(item.unit).add(item.start).toDate()
            const end = moment().startOf(item.unit).add(item.end).toDate()
            picker.$emit('pick', [start, end])
          }
        })),
        disabledDate (time) {
          const current = new Date()
          return time.valueOf() > current.valueOf()
        }
      }
    },
    tableColumns () {
      return [
        {
          hidden: !this.isSupperAdminCompany,
          label: this.$t(`${this.i18nPrefix}.label.company`),
          dataKey: 'companyI18nName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.integrationType`),
          dataKey: 'sync_job_type',
          formatter: (data) => {
            let labelKey = 'otherSync'
            if (data.startsWith(INTEGRATION_TYPE.SYNC_JOB_FROM)) {
              labelKey = 'jobSync'
            } else if (data.startsWith(INTEGRATION_TYPE.SYNC_APPLICANT_TO)) {
              labelKey = 'applicationSync'
            } else if (data.startsWith(INTEGRATION_TYPE.PULL_APPLICANT_FROM)) {
              labelKey = 'applicationPullSync'
            } else if (data.startsWith(INTEGRATION_TYPE.SYNC_EMPLOYEE_FROM)) {
              labelKey = 'employeePullSync'
            } else if (data.startsWith(INTEGRATION_TYPE.SYNC_APPLICANT_STATUS_FROM)) {
              labelKey = 'applicationStatusPullSync'
            } else if (data.startsWith(INTEGRATION_TYPE.SYNC_APPLICANT_STATUS_TO)) {
              labelKey = 'applicationStatusPushSync'
            }
            return this.$t(`${this.i18nPrefix}.label.${labelKey}`)
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.systemName`),
          dataKey: 'system_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.jobId`),
          dataKey: 'job.id',
          formatter: (data, row) => {
            if (!data) {
              return ''
            }
            return `${row.job.id}-${row.job.ats_job_id}`
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.jobName`),
          dataKey: 'jobI18nName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.candidateId`),
          dataKey: 'candidate.id'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.candidateEmail`),
          dataKey: 'candidate.email'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.candidateName`),
          dataKey: 'candidate.name',
          slotName: 'candidateName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.errorDate`),
          dataKey: 'error_time',
          formatter: (data) => {
            return data ? moment(data).format('YYYY-MM-DD HH:mm:ss') : ''
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.syncErrorType`),
          dataKey: 'error_type',
          formatter: (data, row) => {
            return this.$t(`${this.i18nPrefix}.ERROR_TYPE.${data}`)
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.processStatus`),
          dataKey: 'process_status',
          formatter: (data) => {
            return this.$t(`${this.i18nPrefix}.PROCESS_STATUS.${data}`)
          }
        }
      ]
    },
    isSupperAdminCompany () {
      return _.includes([64, 15], this.companyId)
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('apScheduler', [
      'getIntegrationErrorRecordList',
      'markIntegrationErrorRecordProcessed',
      'createExportIntegrationErrorRecordTask',
      'getExportIntegrationErrorRecordTaskInfo']),
    initPage () {
      this.loadErrorRecordList()
    },
    async loadErrorRecordList () {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
          sync_job_type: this.search.sync_job_type,
          start_date: this.search.start_date,
          end_date: this.search.end_date,
          process_status: this.search.process_status,
          root_company_id: this.isSupperAdminCompany ? this.search.root_company_id : this.companyId
        }
        const resp = await this.getIntegrationErrorRecordList(params)
        if (resp) {
          this.pagination.total = resp.total_count
          _.forEach(resp.list_data, item => {
            addI18nProperty(item, 'companyI18nName', 'root_company.cn_name', 'root_company.en_name')
            addI18nProperty(item, 'jobI18nName', 'job.cn_title', 'job.en_title')
          })
          this.errorRecordList = resp.list_data
        }
      } finally {
        this.loading = false
      }
    },
    handleViewErrorDetail (row) {
      this.errorInfoDialog.show = true
      this.errorInfoDialog.content = row.error_info
    },
    handleOpenProfile (row) {
      if (this.isSupperAdminCompany) {
        return
      }
      const candidateId = row.candidate.id
      const jobId = row.job.id
      window.open(`/v-admin/application/profile/resume?candidate_id=${candidateId}&job_id=${jobId}`)
    },
    async handleMarkErrorHasProcessed (row) {
      this.loading = true
      try {
        const params = {
          record_id: row.id
        }
        await this.markIntegrationErrorRecordProcessed(params)
        this.loadErrorRecordList()
      } finally {
        this.loading = false
      }
    },
    handleSearchChange () {
      this.pagination.page = 1
      this.loadErrorRecordList()
    },
    handlePaginationChange () {
      this.loadErrorRecordList()
    },
    formatXmlContent (xml) {
      if (!xml) {
        return ''
      }
      // 去掉多余空白
      xml = xml.trim()
        .replace(/>\s+</g, '><') // 去行间空格
        .replace(/</g, '\n<') // 每个标签前强制换行
        .split('\n')
        .filter(Boolean) // 去掉空行

      let indent = 0; const pad = '  '
      return xml.map(line => {
        if (line.startsWith('</')) indent--
        const prefix = pad.repeat(Math.max(indent, 0))
        if (line.startsWith('<') && !line.startsWith('</') && !line.endsWith('/>')) indent++
        return prefix + line.trim()
      }).join('\n')
    },
    handleExport () {
      this.exportStatus.loading = true
      const exportRewardStatus = {
        finished: false,
        task: null
      }
      exportRewardStatus.finished = false
      exportRewardStatus.task = new AsyncTask(() => this.createExportIntegrationErrorRecordTask({
        sync_job_type: this.search.sync_job_type,
        start_date: this.search.start_date,
        end_date: this.search.end_date,
        process_status: this.search.process_status,
        root_company_id: this.isSupperAdminCompany ? this.search.root_company_id : this.companyId
      }), (taskId) => this.getExportIntegrationErrorRecordTaskInfo({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/v-admin/integration/error-record-export/?task_id=${taskId}&download=1`)
        exportRewardStatus.finished = false
        exportRewardStatus.task = null
        this.exportStatus.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.error-record-container {
  .error-record-list {
    .date-select {
      ::v-deep {
        .el-date-editor {
          .el-range__icon {
            height: inherit;
          }
          .el-range-input {
            line-height: 30;
            height: 30px;
          }
          .el-range-separator  {
            flex: 1;
            display: inline-flex;
            height: 100%;
            justify-content: center;
            align-items: center;
          }
          .el-range__close-icon {
            height: inherit;
          }
        }
      }
    }

    .export-btn {
      border: none;
      background-color: #0096cc;
      color: #fff;
      border-radius: 4px;
      font-size: 14px;
      padding: 9px 20px;
      &:hover {
        background-color: #0af;
      }
      ::v-deep {
        .el-loading-mask {
          .el-loading-spinner {
            margin-top: -14px;

            .loading-spinner {
              .spinner {
                height: 28px;
              }
            }
          }
        }
      }
    }

    .candidate-name {}

    .has-click {
      color: #0096cc;
      &:hover {
        cursor: pointer;
        color: #0096cc;
      }
    }
  }

  .error-info-dialog {
    .error-info {
      background: #f6f8fa;
      color: #24292e;
      padding: 12px 16px;
      border-radius: 4px;
      overflow-x: auto;
      font-size: 14px;
      white-space: pre;
    }
  }
}
</style>
